<section class="contract-mapping-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div class="page-header__copy">
        <h1>{{ title() }}</h1>
        <p>{{ description() }}</p>
      </div>

      <div class="page-header__actions">
        <button type="button" class="app-button app-button--primary" (click)="openCreateForm()">Add Mapping</button>
      </div>
    </header>

    <section class="table-card app-card">
      <div class="toolbar">
        <label class="search-field">
          <span>Search Mapping</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Search company, unit, buyer, contract type, or PSS"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>
      </div>

      @if (toast()) {
        <div class="toast-message" [class.error]="toast()?.type === 'error'" role="status">
          {{ toast()?.message }}
        </div>
      }

      <div class="table-wrap">
        <table class="contract-table">
          <thead>
            <tr>
              <th>Generating Company</th>
              <th>Generating Unit</th>
              <th>Buyer Type</th>
              <th>Buyer Name</th>
              <th>PPA / EPA / Contract Type</th>
              <th>Contract Capacity (MW)</th>
              <th>Contract Rate (Rs/kWh)</th>
              <th>Effective From</th>
              <th>Effective To</th>
              <th>PSS</th>
            </tr>
          </thead>
          <tbody>
            @for (row of displayRows(); track row.id) {
              <tr>
                @if (row.showCompanyCell) {
                  <td class="group-cell" [attr.rowspan]="row.companyRowSpan">{{ row.generatingCompanyName }}</td>
                }
                @if (row.showUnitCell) {
                  <td class="group-cell" [attr.rowspan]="row.unitRowSpan">{{ row.generatingUnitName }}</td>
                }
                <td>{{ row.buyerType }}</td>
                <td>{{ row.buyerName }}</td>
                <td>{{ row.contractType }}</td>
                <td>{{ formatNumber(row.contractCapacityMw) }}</td>
                <td>{{ formatNumber(row.contractRate) }}</td>
                <td>{{ formatDate(row.effectiveFrom) }}</td>
                <td>{{ formatDate(row.effectiveTo) }}</td>
                <td>{{ row.pssName }}</td>
              </tr>
            } @empty {
              <tr>
                <td colspan="10" class="empty-state">No contract mappings found.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button type="button" class="page-button" [class.active]="page === currentPage()" (click)="goToPage(page)">
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>
  </div>

  @if (isFormOpen()) {
    <div class="modal-backdrop" role="presentation">
      <section class="mapping-modal app-card" role="dialog" aria-modal="true" aria-labelledby="contract-mapping-modal-title">
        <div class="modal-header">
          <div>
            <h2 id="contract-mapping-modal-title">Add Contract Mapping</h2>
            <p>Select values from the linked mock data and save the mapping.</p>
          </div>

          <button type="button" class="modal-close" (click)="closeForm()" aria-label="Close contract mapping form">×</button>
        </div>

        <form class="mapping-form" [formGroup]="form" (ngSubmit)="save()">
          <div class="form-grid">
            <label class="form-field">
              <span>Generating Company</span>
              <select class="app-form-select" formControlName="generatingCompanyCode">
                <option value="">Select generating company</option>
                @for (company of generatingCompanies(); track company.code) {
                  <option [value]="company.code">{{ company.name }}</option>
                }
              </select>
            </label>

            <label class="form-field">
              <span>Generating Unit</span>
              <select class="app-form-select" formControlName="generatingUnitCode">
                <option value="">Select generating unit</option>
                @for (unit of filteredUnits(); track unit.code) {
                  <option [value]="unit.code">{{ unit.name }}</option>
                }
              </select>
            </label>

            <label class="form-field">
              <span>Buyer Type</span>
              <select class="app-form-select" formControlName="buyerType">
                <option value="">Select buyer type</option>
                @for (buyerType of buyerTypes(); track buyerType) {
                  <option [value]="buyerType">{{ buyerType }}</option>
                }
              </select>
            </label>

            <label class="form-field">
              <span>Buyer Name</span>
              <select class="app-form-select" formControlName="buyerName">
                <option value="">Select buyer name</option>
                @for (buyer of availableBuyerOptions(); track buyer.code) {
                  <option [value]="buyer.name">{{ buyer.name }}</option>
                }
              </select>
            </label>

            <label class="form-field">
              <span>PPA / EPA / Contract Type</span>
              <select class="app-form-select" formControlName="contractType">
                <option value="">Select contract type</option>
                @for (contractType of contractTypes(); track contractType) {
                  <option [value]="contractType">{{ contractType }}</option>
                }
              </select>
            </label>

            <label class="form-field">
              <span>Contract Capacity (MW)</span>
              <input class="app-form-input" type="number" min="0" step="0.01" formControlName="contractCapacityMw" placeholder="Enter capacity">
            </label>

            <label class="form-field">
              <span>Contract Rate (Rs/kWh)</span>
              <input class="app-form-input" type="number" min="0" step="0.01" formControlName="contractRate" placeholder="Enter rate">
            </label>

            <label class="form-field">
              <span>Effective From</span>
              <input class="app-form-input" type="date" formControlName="effectiveFrom">
            </label>

            <label class="form-field">
              <span>Effective To</span>
              <input class="app-form-input" type="date" formControlName="effectiveTo">
            </label>

            <label class="form-field">
              <span>PSS</span>
              <select class="app-form-select" formControlName="pssCode">
                <option value="">Select PSS</option>
                @for (pss of availablePssOptions(); track pss.code) {
                  <option [value]="pss.code">{{ pss.name }}</option>
                }
              </select>
            </label>
          </div>

          @if (formError()) {
            <div class="form-error" role="alert">{{ formError() }}</div>
          }

          <div class="form-actions">
            <button type="button" class="app-button app-button--secondary" (click)="closeForm()">Cancel</button>
            <button type="submit" class="app-button app-button--primary">Save Mapping</button>
          </div>
        </form>
      </section>
    </div>
  }
</section>
